import React, { useEffect, useRef } from 'react';
import SectionLabel from '../ui/SectionLabel';
import './Services.css';

const services = [
  {
    number: '01',
    category: 'AI Research & Innovation',
    title: 'Advanced AI Research',
    description:
      'Building intelligent AI solutions using machine learning, natural language processing, computer vision, and modern generative AI technologies. Filing patents and publishing research to contribute to the global AI ecosystem.',
    tags: ['Machine Learning', 'NLP', 'Computer Vision', 'Generative AI'],
    featured: true,
  },
  {
    number: '02',
    category: 'Product Development',
    title: 'AI-Powered Platforms',
    description:
      'Building AI-powered platforms for healthcare, finance, logistics, and retail. Offering scalable SaaS solutions for predictive analytics, automation, and customer engagement.',
    tags: ['SaaS Platforms', 'Predictive Analytics', 'Automation', 'CRM'],
    featured: false,
  },
  {
    number: '03',
    category: 'Ethical & Responsible AI',
    title: 'Responsible AI Deployment',
    description:
      'Building AI solutions with a strong focus on transparency, responsible decision-making, and secure data handling. Establishing frameworks for responsible AI deployment and governance.',
    tags: ['AI Governance', 'Fairness', 'Data Privacy', 'Compliance'],
    featured: false,
  },
  {
    number: '04',
    category: 'Partnerships & Collaboration',
    title: 'AI Ecosystem Growth',
    description:
      'Working with enterprises, startups, and academic institutions to co-create AI solutions. Conducting workshops, hackathons, and seminars to promote AI literacy.',
    tags: ['Hackathons', 'University Tie-ups', 'Partnerships'],
    featured: false,
  },
  {
    number: '05',
    category: 'Talent Development',
    title: 'AI Talent Academy',
    description:
      'Running in-house AI academies and training programs. Partnering with academic institutions to develop future-ready AI talent through training, mentorship, and hands-on learning.',
    tags: ['AI Training', 'Academies', 'University Collaboration'],
    featured: false,
  },
  {
    number: '06',
    category: 'AI Security',
    title: 'AI Systems Protection',
    description:
      'Protecting AI systems from threats, vulnerabilities, and misuse. Safeguarding digital assets with real-time threat detection and faster response.',
    tags: ['Threat Detection', 'Vulnerability Assessment', 'AI Monitoring'],
    featured: false,
  },
  {
    number: '07',
    category: 'Social Impact',
    title: 'AI for Social Good',
    description:
      'Leveraging AI for healthcare access, sustainable agriculture, and environmental protection. Supporting small businesses with affordable AI-driven solutions.',
    tags: ['Healthcare Access', 'Sustainability', 'Small Business AI'],
    featured: false,
  },
];

const Services: React.FC = () => {
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const cards = gridRef.current?.querySelectorAll<HTMLElement>('.services__card');
    if (!cards) return;

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).classList.add('services__card--visible');
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );

    cards.forEach((card) => obs.observe(card));
    return () => obs.disconnect();
  }, []);

  return (
    <section className="services" id="services">

      <div className="services__bg-svg">
        <svg viewBox="0 0 900 600" preserveAspectRatio="xMidYMid slice">
          <defs>
            <pattern id="diagSvc" width="28" height="28" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
              <line x1="0" y1="0" x2="0" y2="28" stroke="#E8540A" strokeWidth="0.5" opacity="0.06"/>
            </pattern>
          </defs>
          <rect width="900" height="600" fill="url(#diagSvc)"/>
          <text x="500" y="520" fontSize="340" fontWeight="900"
            fill="#E8540A" opacity="0.025" letterSpacing="-12" fontFamily="sans-serif">07</text>
        </svg>
      </div>

      <div className="services__content">

        <div className="services__header">
          <SectionLabel text="What We Do · Core Activities" />
          <div className="services__header-row">
            <h2 className="services__title">
              Our <span className="services__title-accent">Services</span>
            </h2>
            <p className="services__subtitle">
              Production-ready AI systems built for real-world impact across industries.
            </p>
          </div>
        </div>

        <div className="services__grid" ref={gridRef}>
          {services.map((s, i) => (
            <div
              className={`services__card${s.featured ? ' services__card--featured' : ''}`}
              key={s.number}
              data-num={s.number}
              style={{ transitionDelay: `${i * 0.07}s` }}
            >
              <div className="services__card-top">
                <span className="services__card-num">{s.number}</span>
                <span className="services__card-category">{s.category}</span>
              </div>
              <h3 className="services__card-title">{s.title}</h3>
              <p className="services__card-desc">{s.description}</p>
              <div className="services__card-bottom">
                <div className="services__card-tags">
                  {s.tags.map(tag => (
                    <span className="services__tag" key={tag}>{tag}</span>
                  ))}
                </div>
                <span className="services__card-arrow">→</span>
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default Services;